/**
 * Tabbed Character Sheet - Main Layout Container
 * Research-driven mobile-first responsive design
 *
 * Architecture:
 * - Mobile (< 1024px): Bottom tab navigation
 * - Desktop (>= 1024px): Left sidebar navigation
 * - Always-visible vitals bar (HUD pattern)
 * - Lazy-loaded tab content with Suspense
 * - Framer Motion transitions
 */

import React, { useState, useEffect, useCallback, useRef, Suspense, lazy } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import MobileTabNavigation from './MobileTabNavigation';
import DesktopSidebar from './DesktopSidebar';
import StickyVitalsBar from './StickyVitalsBar';
import { IconSettings } from '../icons';
import AppHeader from '../layout/AppHeader';
import { cn } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';
import { useDaggerheartData, useHomebrewOverride } from '../../context/DataContext';
import useHomebrewLoader from '../../hooks/useHomebrewLoader';
import { PartyVitalsProvider } from '../../context/PartyVitalsContext';
import { getArmorInfo, getWeaponInfo } from '../../utils/equipmentHelpers';
import {
  calculateArmorScore,
  calculateEvasion,
  calculateProficiency,
  calculateDamageThresholds
} from '../../utils/characterCalculations';
import useCharacterModals from '../../hooks/useCharacterModals';
import useCharacterCampaign from '../../hooks/useCharacterCampaign';
import useEquipmentManager from '../../hooks/useEquipmentManager';
import useVitalsManager from '../../hooks/useVitalsManager';
import useHPDamageMode from '../../hooks/useHPDamageMode';
import { db, rtdb } from '../../firebase/config';
import { ref as rtdbRef, set as rtdbSet, onDisconnect, serverTimestamp } from 'firebase/database';

// Import token services
import {
  getTokensForCard,
  activateFlightTokens,
  activateDisguiseTokens,
  activateInvisibility,
  refreshTokens,
  getWildSurgeDieValue,
  getZoneProtectionDieValue
} from '../../services/SimpleTokenService';

// Import centralized migration service
import { migrateCharacter, getSpellcastTrait } from '../../services/characterMigrationService';
import DomainCardTokens from '../DomainCardTokens';

// Import modal managers
import Modal from '../common/Modal';
import DomainCardManager from '../DomainCardManager';
import InventoryManager from '../InventoryManager';
import LootCollectionManager from '../LootCollectionManager';
import HarvestIngredientsManager from '../HarvestIngredientsManager';
import UnifiedBeastFeastManager from '../CollaborativeBeastFeastManager';
import CampaignFrameRenderer from '../campaign-frames/CampaignFrameRenderer';
import useFrameSession from '../../hooks/useFrameSession';
import BerrysRestaurantManager from '../BerrysRestaurantManager';
import DowntimeManager from '../DowntimeManager';
import EquipmentBrowser from '../EquipmentBrowser';
import DeathManager from '../DeathManager';

// Lazy load tab components for better performance
const CombatTab = lazy(() => import('./tabs/CombatTab'));
const InventoryTab = lazy(() => import('./tabs/InventoryTab'));
const CharacterTab = lazy(() => import('./tabs/CharacterTab'));

// Loading skeleton for tab content
function TabLoadingSkeleton() {
  return (
    <div className="p-6 space-y-4 animate-pulse">
      <div className="h-8 bg-surface-2 rounded w-1/3"></div>
      <div className="h-32 bg-surface-2 rounded"></div>
      <div className="h-24 bg-surface-2 rounded"></div>
      <div className="h-48 bg-surface-2 rounded"></div>
    </div>
  );
}

export default function TabbedCharacterSheet({
  characterData,
  onCharacterUpdate,
  onExit
}) {
  const navigate = useNavigate();
  const allData = useDaggerheartData();
  const [activeTab, setActiveTab] = useState('combat');
  const [activationMessage, setActivationMessage] = useState(null);
  const [showSettingsDropdown, setShowSettingsDropdown] = useState(false);

  // Use character modals hook for all modal states
  const modals = useCharacterModals();

  // Campaign frame session state (ephemeral)
  const { sessionState: frameSessionState, updateSessionState: updateFrameSession } = useFrameSession(characterData?.campaignFrame || 'core');

  // Get authenticated user and campaign data
  const { user } = useAuth();
  const { characterCampaignId, campaignCookbook } = useCharacterCampaign(characterData?.id, user);

  // Load and register homebrew mechanics so they are available when the sheet renders
  const setHomebrewCardOverrides = useHomebrewOverride();
  const { homebrewCards, loaded: homebrewLoaded } = useHomebrewLoader(user?.uid);

  useEffect(() => {
    if (homebrewLoaded && Object.keys(homebrewCards).length > 0) {
      setHomebrewCardOverrides(homebrewCards);
    }
  }, [homebrewCards, homebrewLoaded, setHomebrewCardOverrides]);

  // Local character state for immediate UI updates
  const [character, setCharacter] = useState(characterData);

  // Ref that always has the latest character (for use in callbacks that may have stale closures)
  const characterRef = useRef(character);
  useEffect(() => {
    characterRef.current = character;
  }, [character]);

  // Separate companion state (like old CharacterSheet.js)
  const [companion, setCompanion] = useState(null);

  // LOCAL STATE for session-only mechanics (not stored on character, avoids Firebase sync issues)
  // Pattern: Same as useHPDamageMode - these don't persist to character object
  const [channelRawPowerBonus, setChannelRawPowerBonus] = useState(0);

  // Conjure Swarm - Beetles mode (reduces next damage by 1 threshold)
  const [beetlesActive, setBeetlesActive] = useState(false);
  const [showBeetlesMaintainOption, setShowBeetlesMaintainOption] = useState(false);

  // Pending spell attack - allows domain cards to trigger rolls via DiceRoller
  // When set, DiceRoller will auto-configure and execute the spell attack
  const [pendingSpellAttack, setPendingSpellAttack] = useState(null);
  // Shape: { spellName, difficulty, onSuccess: { damage, damageType, hopeCost } }

  // Second Wind - arms self-healing on next successful attack
  // Shape: { type: 'stress' | 'hp', cardKey: 'second-wind' }
  const [secondWindPending, setSecondWindPending] = useState(null);

  // Strategic Approach - toggles advantage on next roll
  const [strategyAdvantageActive, setStrategyAdvantageActive] = useState(false);
  // Strategic Approach - bonus d8s to add to next damage roll
  const [strategyBonusDice, setStrategyBonusDice] = useState(0);

  // Signature Move - d20 Hope Die for next roll, clear Stress on success
  const [signatureMoveActive, setSignatureMoveActive] = useState(false);

  // Deft Maneuvers - +1 attack bonus after sprint
  const [deftManeuversActive, setDeftManeuversActive] = useState(false);

  // Wild Surge - progressive die bonus to action rolls (1-6, increases after each roll)
  const [wildSurgeDie, setWildSurgeDie] = useState(0);

  // Sigil of Retribution - rolled d8s bonus to add to next damage roll
  const [sigilRetributionBonus, setSigilRetributionBonus] = useState(0);

  // Veil of Night - Hidden status + advantage on attacks through darkness
  // Clears when another spell is cast
  const [veilOfNightActive, setVeilOfNightActive] = useState(false);

  // Midnight Spirit - summoned spirit that can attack (dissipates on successful attack)
  const [midnightSpiritActive, setMidnightSpiritActive] = useState(false);

  // Orc Tusks - toggle for +1d6 damage on melee hit (costs 1 Hope)
  // Lifted from DiceRoller so AbilitiesPanel can also toggle it
  const [useTusks, setUseTusks] = useState(false);

  // Faun Kick - toggle for +2d6 damage on melee hit (costs 1 Stress)
  // Lifted from DiceRoller so AbilitiesPanel can also toggle it
  const [useFaunKick, setUseFaunKick] = useState(false);

  // Track when local updates are in progress to avoid sync conflicts
  const isLocalUpdateInProgress = useRef(false);
  const lastSyncedCharacterId = useRef(characterData?.id);

  // Sync local character state with incoming characterData prop
  // Skip sync during local updates to avoid race conditions with Firebase
  useEffect(() => {
    if (!characterData) return;

    // Always sync when switching to a different character
    if (characterData.id !== lastSyncedCharacterId.current) {
      setCharacter(characterData);
      lastSyncedCharacterId.current = characterData.id;
      return;
    }

    // Skip sync if a local update is in progress
    if (isLocalUpdateInProgress.current) {
      return;
    }

    setCharacter(characterData);
  }, [characterData]);

  // Sync progressive die states with character data on load
  // This ensures DiceRoller knows about active Wild Surge/Zone of Protection from previous session
  // Use a ref to track the last value and only update if changed (prevents re-render loops)
  const lastWildSurgeDie = useRef(0);
  useEffect(() => {
    if (!character) return;

    const wildSurgeValue = getWildSurgeDieValue(character);
    // Only update state if value actually changed to prevent infinite loops
    if (wildSurgeValue !== lastWildSurgeDie.current) {
      lastWildSurgeDie.current = wildSurgeValue;
      setWildSurgeDie(wildSurgeValue);
    }
  }, [character?.cardResources]); // Re-sync when cardResources changes

  // Track if migrations have been applied (prevent re-runs)
  const migrationsApplied = useRef(false);

  // Track current character ID to detect actual character changes
  const currentCharacterId = useRef(null);

  // Stable callback for character updates (prevents infinite loops)
  // Updates BOTH local state AND parent/Firebase to ensure UI stays in sync
  const stableCharacterUpdate = useCallback((updatedChar) => {
    // Mark that a local update is in progress to prevent Firebase sync from overwriting
    isLocalUpdateInProgress.current = true;

    // Update ref immediately so subsequent updates have latest data
    characterRef.current = updatedChar;

    // Update local state immediately for responsive UI
    setCharacter(updatedChar);
    // Propagate to parent/Firebase
    if (onCharacterUpdate) {
      onCharacterUpdate(updatedChar);
    }
    // Clear the flag after Firebase has time to sync (1 second delay)
    setTimeout(() => {
      isLocalUpdateInProgress.current = false;
    }, 1000);
  }, [onCharacterUpdate]);

  // MERGE UPDATE PATTERN: For components that only want to update specific fields
  // without accidentally overwriting other fields with stale data.
  // Usage: mergeCharacterUpdate({ loadout, vault }) - only specify changed fields
  const mergeCharacterUpdate = useCallback((partialUpdates) => {
    // Always merge with the LATEST character from ref (not stale props)
    const latestChar = characterRef.current;
    const mergedChar = { ...latestChar, ...partialUpdates };
    stableCharacterUpdate(mergedChar);
  }, [stableCharacterUpdate]);

  // Data Migrations: Centralized migration service handles all character schema updates
  // Runs once per character load to migrate old character data to current schema
  useEffect(() => {
    if (!characterData || !characterData.id || migrationsApplied.current) return;

    const { character: migratedChar, needsSave, migrationsRun } = migrateCharacter(characterData, allData);

    if (needsSave) {
      console.log('Migrations applied:', migrationsRun);
      setCharacter(migratedChar);
      stableCharacterUpdate(migratedChar);
    }
    migrationsApplied.current = true;
  }, [characterData?.id, allData?.subclasses, stableCharacterUpdate]);

  // Companion Firestore Listener: Real-time sync for Beastbound Rangers
  // Separate from character data sync to avoid race conditions
  useEffect(() => {
    const charId = characterData?.id;
    const subclass = characterData?.subclass;

    // Only run for Beastbound Rangers
    if (subclass !== 'beastbound' || !charId) {
      return;
    }

    // Check if this is the same character we already have a listener for
    const isSameCharacter = currentCharacterId.current === charId;

    if (isSameCharacter) {
      return;
    }

    // Update the ref to track this character
    currentCharacterId.current = charId;

    let unsubscribe;

    const setupCompanionListener = async () => {
      try {
        // Dynamic import to avoid loading Firestore until needed
        const { collection, query, limit, onSnapshot } = await import('firebase/firestore');
        const companionCollectionRef = collection(db, 'characters', charId, 'companion');
        const q = query(companionCollectionRef, limit(1));

        unsubscribe = onSnapshot(
          q,
          (snapshot) => {
            if (!snapshot.empty) {
              const companionDoc = snapshot.docs[0];
              const companionData = {
                id: companionDoc.id,
                ...companionDoc.data()
              };

              // Set companion state separately (matches old CharacterSheet.js pattern)
              setCompanion(companionData);
            } else {
              // No companion found - clear companion data
              setCompanion(null);
            }
          },
          (error) => {
            console.error('[Companion Listener] Error:', error);
            toast.error('Failed to load companion data');
          }
        );
      } catch (error) {
        console.error('[Companion Listener] Setup failed:', error);
      }
    };

    setupCompanionListener();

    // Cleanup function to unsubscribe when component unmounts or character changes
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
      // Only reset ref if we're actually changing characters
      // Don't reset on every effect re-run (React StrictMode double-mount)
      if (currentCharacterId.current === charId) {
        currentCharacterId.current = null;
      }
    };
  }, [characterData?.id, characterData?.subclass]);

  // Player Presence Tracking - Broadcast to GM Dashboard when viewing character sheet
  useEffect(() => {
    // Only track if we have a campaign, character, and user
    if (!characterCampaignId || !characterData?.id || !user) {
      return;
    }

    // Set up presence in RTDB at campaigns/{campaignId}/activePlayers/{userId}
    const presenceRef = rtdbRef(rtdb, `campaigns/${characterCampaignId}/activePlayers/${user.uid}`);

    const presenceData = {
      characterId: characterData.id,
      characterName: characterData.name || 'Unknown Character',
      playerName: user.displayName || user.email || 'Unknown Player',
      lastSeen: serverTimestamp(),
      viewingSheet: true
    };

    // Set presence data
    rtdbSet(presenceRef, presenceData).catch((error) => {
      console.error('[Presence Tracking] Error setting presence:', error);
    });

    // Set up auto-removal on disconnect
    onDisconnect(presenceRef).remove().catch((error) => {
      console.error('[Presence Tracking] Error setting disconnect cleanup:', error);
    });

    // Cleanup: Remove presence when component unmounts or campaign/character changes
    return () => {
      rtdbSet(presenceRef, null).catch((error) => {
        console.error('[Presence Tracking] Error removing presence:', error);
      });
    };
  }, [characterCampaignId, characterData?.id, characterData?.name, user]);

  // HP damage mode management
  const { hpDamageMode, toggleHPDamageMode, handleHPDamageWithMode } = useHPDamageMode(character);

  // Vitals management with stress overflow to HP (including companion vitals)
  const { updateVitals, markArmor, toggleCondition } = useVitalsManager(
    character,
    setCharacter,
    stableCharacterUpdate, // Use stableCharacterUpdate to get consumable field protection
    modals.setIsFacingDeath,
    hpDamageMode,
    handleHPDamageWithMode,
    getSpellcastTrait,
    allData,
    companion,
    setCompanion,
    user,
    characterRef, // Pass ref to always get latest character state
    // Conjure Swarm - Beetles mode (session-local state)
    beetlesActive,
    setBeetlesActive,
    setShowBeetlesMaintainOption  // Show inline option in StickyVitalsBar after beetles absorb damage
  );

  // Derive class/subclass/ancestry/community info from allData (needed for calculations)
  const classInfo = allData.classes?.[character.class];

  // Get armor and weapon info for defense calculations
  const equippedArmorId = character.equipped?.armor || character.equipment?.armor;
  const armorData = getArmorInfo(equippedArmorId, allData, character);
  const secondaryWeaponId = character.equipped?.secondaryWeapon || character.equipment?.secondaryWeapon;
  const secondaryWeaponData = getWeaponInfo(secondaryWeaponId, allData, character);

  // Calculate proficiency using stored value + class bonuses
  const currentProficiency = calculateProficiency(character, classInfo);

  // Calculate evasion using stored value + armor modifiers
  const evasion = calculateEvasion(character, armorData);

  // Calculate armor score from equipped armor (including domain card bonuses)
  const currentArmorScore = calculateArmorScore(armorData, secondaryWeaponData, character);

  // Calculate damage thresholds using armor + level + subclass bonuses
  const thresholds = calculateDamageThresholds(armorData, character.level || 1, character);

  // Get spellcast trait from subclass data (uses helper function for consistency)
  const spellcastTrait = getSpellcastTrait(character, allData);

  // Use equipment manager hook
  const { equipItem, unequipItem } = useEquipmentManager(character, onCharacterUpdate, allData);

  // Get equipped equipment (with fallback to old equipment field)
  const primaryWeapon = getWeaponInfo(
    character.equipped?.primaryWeapon || character.equipment?.primaryWeapon,
    allData,
    character
  );
  const secondaryWeapon = secondaryWeaponData;
  const equippedArmor = armorData;
  const subclassInfo = allData.subclasses?.[character.subclass];
  const ancestryInfo = Array.isArray(character.ancestry)
    ? character.ancestry.map(a => allData.ancestries?.[a.toLowerCase()]).filter(Boolean)
    : allData.ancestries?.[character.ancestry?.toLowerCase()];
  const communityInfo = allData.communities?.[character.community?.toLowerCase()];

  // Get displayed ancestry features (matching old CharacterSheet logic)
  const displayedAncestryFeatures = character.ancestryFeatures || [];

  // Settings handlers
  const handleSessionRefresh = () => {
    // Mark that a local update is in progress to prevent sync conflicts
    isLocalUpdateInProgress.current = true;

    // Get spellcast trait using the helper function (allData.subclasses is an array)
    const spellcastTrait = getSpellcastTrait(character, allData);
    const refreshedCharacter = refreshTokens(character, 'session', spellcastTrait);
    setCharacter(refreshedCharacter);
    onCharacterUpdate(refreshedCharacter);

    // Clear the flag after Firebase has time to sync (1 second delay)
    setTimeout(() => {
      isLocalUpdateInProgress.current = false;
    }, 1000);

    toast.success('✨ Session tokens refreshed!', {
      icon: '🔄',
      duration: 2000
    });
    setShowSettingsDropdown(false);
  };

  const handleEditCharacter = () => {
    if (character.level > 1) {
      toast((t) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <div>
            <strong>⚠️ Warning:</strong> This character is level {character.level}.
            Editing core character traits may overwrite level progression, domain cards, and other advancement.
          </div>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button
              onClick={() => {
                toast.dismiss(t.id);
              }}
              style={{
                padding: '6px 12px',
                background: '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              onClick={() => {
                navigate(`/characters/${character.id}/build?level=1`);
                toast.dismiss(t.id);
              }}
              style={{
                padding: '6px 12px',
                background: '#dc2626',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Edit Anyway
            </button>
          </div>
        </div>
      ), {
        duration: Infinity,
        style: {
          maxWidth: '500px'
        }
      });
      setShowSettingsDropdown(false);
      return;
    }

    // Navigate to unified character builder with level 1
    navigate(`/characters/${character.id}/build?level=1`);
    setShowSettingsDropdown(false);
  };

  // Handle weapon resource changes
  const handleWeaponResourceChange = (resourceId, newValue) => {
    if (!onCharacterUpdate) return;

    const updatedCharacter = { ...character };
    if (!updatedCharacter.resources) updatedCharacter.resources = {};

    if (updatedCharacter.resources[resourceId]) {
      updatedCharacter.resources[resourceId] = {
        ...updatedCharacter.resources[resourceId],
        current: newValue
      };
      // Update both local state and parent/Firebase
      setCharacter(updatedCharacter);
      onCharacterUpdate(updatedCharacter);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'combat':
        return (
          <CombatTab
            character={character}
            allData={allData}
            onCharacterUpdate={stableCharacterUpdate}
            getSpellcastTrait={getSpellcastTrait}
            setSelectedCard={modals.setSelectedCard}
            setIsManagingCards={modals.setIsManagingCards}
            setIsLootCollecting={modals.setIsLootCollecting}
            setIsHarvesting={modals.setIsHarvesting}
            setIsFeasting={modals.setIsFeasting}
            setIsAtBerrysRestaurant={modals.setIsAtBerrysRestaurant}
            setIsDowntiming={modals.setIsDowntiming}
            setActiveFrameMechanic={modals.setActiveFrameMechanic}
            campaignId={characterCampaignId}
            toggleHPDamageMode={toggleHPDamageMode}
            companion={companion}
            classInfo={classInfo}
            subclassInfo={subclassInfo}
            ancestryInfo={ancestryInfo}
            communityInfo={communityInfo}
            displayedAncestryFeatures={displayedAncestryFeatures}
            homebrewFeatures={character.homebrewFeatures || []}
            channelRawPowerBonus={channelRawPowerBonus}
            setChannelRawPowerBonus={setChannelRawPowerBonus}
            beetlesActive={beetlesActive}
            setBeetlesActive={setBeetlesActive}
            // Pending spell attack (allows domain cards to trigger DiceRoller)
            pendingSpellAttack={pendingSpellAttack}
            setPendingSpellAttack={setPendingSpellAttack}
            // Second Wind (arms self-healing on next successful attack)
            secondWindPending={secondWindPending}
            setSecondWindPending={setSecondWindPending}
            // Strategic Approach (advantage toggle + bonus d8s)
            strategyAdvantageActive={strategyAdvantageActive}
            setStrategyAdvantageActive={setStrategyAdvantageActive}
            strategyBonusDice={strategyBonusDice}
            setStrategyBonusDice={setStrategyBonusDice}
            // Signature Move (d20 Hope Die, clear Stress on success)
            signatureMoveActive={signatureMoveActive}
            setSignatureMoveActive={setSignatureMoveActive}
            // Deft Maneuvers (+1 attack bonus after sprint)
            deftManeuversActive={deftManeuversActive}
            setDeftManeuversActive={setDeftManeuversActive}
            // Wild Surge (+die value to action rolls, increases each roll)
            wildSurgeDie={wildSurgeDie}
            setWildSurgeDie={setWildSurgeDie}
            // Sigil of Retribution (bonus d8s rolled, add to next damage)
            sigilRetributionBonus={sigilRetributionBonus}
            setSigilRetributionBonus={setSigilRetributionBonus}
            // Veil of Night (Hidden + advantage on attacks)
            veilOfNightActive={veilOfNightActive}
            setVeilOfNightActive={setVeilOfNightActive}
            // Midnight Spirit (summoned spirit, dissipates on successful attack)
            midnightSpiritActive={midnightSpiritActive}
            setMidnightSpiritActive={setMidnightSpiritActive}
            // Orc Tusks (+1d6 damage toggle, costs 1 Hope)
            useTusks={useTusks}
            setUseTusks={setUseTusks}
            // Faun Kick (+2d6 damage toggle, costs 1 Stress)
            useFaunKick={useFaunKick}
            setUseFaunKick={setUseFaunKick}
            setActiveTab={setActiveTab}
          />
        );
      case 'inventory':
        return (
          <InventoryTab
            character={character}
            allData={allData}
            onCharacterUpdate={stableCharacterUpdate}
            activeInventoryTab={modals.activeInventoryTab}
            setActiveInventoryTab={modals.setActiveInventoryTab}
            setIsManagingInventory={modals.setIsManagingInventory}
            primaryWeapon={primaryWeapon}
            secondaryWeapon={secondaryWeapon}
            equippedArmor={equippedArmor}
            unequipItem={unequipItem}
            equipItem={equipItem}
            handleWeaponResourceChange={handleWeaponResourceChange}
            campaignCookbook={campaignCookbook}
          />
        );
      case 'character':
        return (
          <CharacterTab
            character={character}
            allData={allData}
            onCharacterUpdate={stableCharacterUpdate}
            currentProficiency={currentProficiency}
            classInfo={classInfo}
            subclassInfo={subclassInfo}
            ancestryInfo={ancestryInfo}
            communityInfo={communityInfo}
            displayedAncestryFeatures={displayedAncestryFeatures}
            openFeature={modals.openFeature}
            toggleFeature={modals.toggleFeature}
            spellcastTrait={spellcastTrait}
            companion={companion}
            setIsManagingCards={modals.setIsManagingCards}
          />
        );
      default:
        return <div>Tab not found</div>;
    }
  };

  return (
    <PartyVitalsProvider campaignId={characterCampaignId} character={character}>
    <div className="bg-background min-h-screen text-gray-100">
      {/* Header Banner - Fixed at top, absolutely locked in place (Google Photos style) */}
      <AppHeader
        pageActions={
          <div className="relative">
            <button
              onClick={() => setShowSettingsDropdown(!showSettingsDropdown)}
              className="p-1.5 sm:p-2 text-gray-400 hover:text-red-400 transition-colors duration-200"
              title="Settings"
            >
              <IconSettings className="w-5 h-5" />
            </button>

            {showSettingsDropdown && (
              <div className="absolute right-0 mt-2 w-48 bg-gradient-to-br from-gray-900 to-black rounded-md shadow-2xl z-50 border-2 border-red-700">
                <div className="py-1">
                  <button
                    onClick={handleSessionRefresh}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-red-900 hover:text-white transition-colors duration-200"
                  >
                    New Session
                  </button>
                  <button
                    onClick={handleEditCharacter}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-red-900 hover:text-white transition-colors duration-200"
                  >
                    Edit Character
                  </button>
                  <button
                    onClick={() => {
                      navigate(`/characters/${character.id}/build?level=${character.level + 1}`);
                      setShowSettingsDropdown(false);
                    }}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-red-900 hover:text-white transition-colors duration-200"
                  >
                    Level Up
                  </button>
                </div>
              </div>
            )}
          </div>
        }
      />

      {/* Character Sheet Container - Padding for fixed header */}
      <div className="min-h-screen pt-[73px]">
        {/* Desktop Sidebar (>= 1024px) */}
        <DesktopSidebar
          activeTab={activeTab}
          onTabChange={setActiveTab}
          character={character}
        />

      {/* Main Content Area */}
      <main
        className={cn(
          // Mobile: full width with bottom padding for tabs
          'pb-20 lg:pb-0',
          // Desktop: left margin for sidebar (always 256px)
          'lg:ml-sidebar',
          // Prevent content from being cut off when viewport shrinks
          'min-w-0 max-w-full flex-1',
          // Rounded top-left corner on desktop (Google Photos style)
          'lg:rounded-tl-2xl'
        )}
      >
        {/* Sticky Vitals Bar - Always Visible HUD */}
        <StickyVitalsBar
          character={character}
          companion={companion}
          updateVitals={updateVitals}
          toggleCondition={toggleCondition}
          hpDamageMode={hpDamageMode}
          toggleHPDamageMode={toggleHPDamageMode}
          currentProficiency={currentProficiency}
          evasion={evasion}
          armorScore={currentArmorScore}
          thresholds={thresholds}
          markArmor={markArmor}
          spellcastTrait={spellcastTrait}
          // Conjure Swarm - Beetles mode
          beetlesActive={beetlesActive}
          setBeetlesActive={setBeetlesActive}
          showBeetlesMaintainOption={showBeetlesMaintainOption}
          setShowBeetlesMaintainOption={setShowBeetlesMaintainOption}
        />

        {/* Tab Content with Suspense */}
        <div
          id={`${activeTab}-panel`}
          role="tabpanel"
          aria-labelledby={`${activeTab}-tab`}
          className={cn(
            "px-4 py-4 max-w-7xl mx-auto",
            // Mobile: extra padding when companion exists (for second vitals row)
            companion ? "pt-36 lg:pt-36" : "pt-28 lg:pt-36"
          )}
        >
          <Suspense fallback={<TabLoadingSkeleton />}>
            {renderTabContent()}
          </Suspense>
        </div>
      </main>

        {/* Mobile Bottom Tab Navigation (< 1024px) */}
        <MobileTabNavigation activeTab={activeTab} onTabChange={setActiveTab} />
      </div>

      {/* Modal Components */}
      {modals.selectedCard && (
        <Modal
          title={modals.selectedCard.name}
          onClose={() => modals.setSelectedCard(null)}
        >
          <p className="font-semibold text-indigo-400 mb-2 capitalize">
            {modals.selectedCard.domain} - {modals.selectedCard.type}
          </p>
          <p className="text-gray-300 whitespace-pre-wrap">{modals.selectedCard.text}</p>
          <p className="mt-4 text-sm text-gray-400">
            Recall Cost: {modals.selectedCard.recallCost} Stress
          </p>

          {/* Token Management */}
          {(() => {
            const spellcastTrait = getSpellcastTrait(character, allData);
            const tokens = getTokensForCard(modals.selectedCard.id, character, spellcastTrait);
            if (tokens.length === 0) return null;

            return (
              <div className="mt-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-700">
                <h4 className="font-semibold text-yellow-800 dark:text-yellow-200 mb-3">Token Management</h4>
                {tokens.map(token => {
                  const cardResources = character.cardResources?.[modals.selectedCard.id] || {};
                  const currentToken = cardResources[token.id] || token;

                  return (
                    <div key={token.id} className="mb-4 last:mb-0">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          {token.name}: {currentToken.current}/{currentToken.maximum}
                        </span>
                        <div className="flex gap-2">
                          {/* Special Actions */}
                          {token.requiresActivation && modals.selectedCard.id === 'flight' && (
                            <button
                              onClick={() => {
                                const updatedCharacter = activateFlightTokens(character, modals.selectedCard.id);
                                setCharacter(updatedCharacter);
                                onCharacterUpdate(updatedCharacter);
                              }}
                              className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
                            >
                              Activate Flight
                            </button>
                          )}
                          {token.requiresActivation && modals.selectedCard.id === 'uncanny-disguise' && (
                            <button
                              onClick={() => {
                                const updatedCharacter = activateDisguiseTokens(character, modals.selectedCard.id);
                                setCharacter(updatedCharacter);
                                onCharacterUpdate(updatedCharacter);
                              }}
                              className="px-2 py-1 text-xs bg-purple-500 text-white rounded hover:bg-purple-600"
                            >
                              Mark Stress & Activate
                            </button>
                          )}
                          {token.requiresActivation && modals.selectedCard.id === 'invisibility' && (
                            <button
                              onClick={() => {
                                const spellcastTraitName = getSpellcastTrait(character, allData);
                                const result = activateInvisibility(character, spellcastTraitName);
                                setCharacter(result.character);
                                onCharacterUpdate(result.character);
                                // Show roll result message in modal
                                setActivationMessage(result.message);
                                // Clear message after 5 seconds
                                setTimeout(() => setActivationMessage(null), 5000);
                              }}
                              className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
                            >
                              Spellcast Roll + Activate
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Token Visual Display */}
                      <DomainCardTokens
                        card={modals.selectedCard}
                        character={character}
                        updateCharacter={(updatedChar) => {
                          setCharacter(updatedChar);
                          onCharacterUpdate(updatedChar);
                        }}
                        size="normal"
                        showEmpty={true}
                        spellcastTrait={spellcastTrait}
                        view="details"
                      />

                      {/* Activation Text */}
                      {token.activationText && (
                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-2 italic">
                          {token.activationText}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })()}

          {/* Activation Message Display */}
          {activationMessage && (
            <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
              <div className="flex items-start gap-2">
                <div className="text-blue-600 dark:text-blue-400 text-sm">🎲</div>
                <div className="text-sm text-blue-800 dark:text-blue-200 whitespace-pre-wrap">
                  {activationMessage}
                </div>
              </div>
            </div>
          )}
        </Modal>
      )}

      {modals.isManagingCards && (
        <Modal
          title="Domain Card Loadout & Vault"
          onClose={() => modals.setIsManagingCards(false)}
        >
          <DomainCardManager
            character={character}
            updateCharacter={mergeCharacterUpdate}
            onManagerClose={() => modals.setIsManagingCards(false)}
          />
        </Modal>
      )}

      {modals.isManagingInventory && (
        <Modal
          title="Inventory Management"
          onClose={() => modals.setIsManagingInventory(false)}
        >
          <InventoryManager
            character={character}
            updateCharacter={onCharacterUpdate}
            onManagerClose={() => modals.setIsManagingInventory(false)}
          />
        </Modal>
      )}

      {modals.isLootCollecting && (
        <Modal
          title="🏺 Discover Loot"
          onClose={() => modals.setIsLootCollecting(false)}
        >
          <LootCollectionManager
            character={character}
            updateCharacter={onCharacterUpdate}
            onManagerClose={() => modals.setIsLootCollecting(false)}
          />
        </Modal>
      )}

      {modals.isHarvesting && (
        <Modal
          title="🦌 Harvest Ingredients"
          onClose={() => modals.setIsHarvesting(false)}
        >
          <HarvestIngredientsManager
            character={character}
            updateCharacter={onCharacterUpdate}
            onManagerClose={() => modals.setIsHarvesting(false)}
          />
        </Modal>
      )}

      {modals.isFeasting && (
        <UnifiedBeastFeastManager
          character={character}
          campaignId={characterCampaignId}
          updateCharacter={onCharacterUpdate}
          onManagerClose={() => modals.setIsFeasting(false)}
        />
      )}

      {modals.isAtBerrysRestaurant && (
        <Modal
          title="🍇 Berry's Restaurant"
          onClose={() => modals.setIsAtBerrysRestaurant(false)}
        >
          <BerrysRestaurantManager
            character={character}
            updateCharacter={onCharacterUpdate}
            onManagerClose={() => modals.setIsAtBerrysRestaurant(false)}
          />
        </Modal>
      )}

      {modals.isDowntiming && (
        <Modal
          title="🏕️ Downtime"
          onClose={() => modals.setIsDowntiming(false)}
        >
          <DowntimeManager
            character={character}
            updateCharacter={onCharacterUpdate}
            onManagerClose={() => modals.setIsDowntiming(false)}
          />
        </Modal>
      )}

      {modals.activeFrameMechanic && character?.campaignFrame && (
        <Modal
          title="Campaign Mechanic"
          onClose={() => modals.setActiveFrameMechanic(null)}
        >
          <CampaignFrameRenderer
            frameId={character.campaignFrame}
            mechanicId={modals.activeFrameMechanic}
            character={character}
            updateCharacter={onCharacterUpdate}
            allData={allData}
            sessionState={frameSessionState}
            setSessionState={updateFrameSession}
            onClose={() => modals.setActiveFrameMechanic(null)}
            campaignId={characterCampaignId}
            user={user}
          />
        </Modal>
      )}

      {modals.isBrowsingEquipment && (
        <Modal
          title="⚔️ Equipment Database"
          onClose={() => modals.setIsBrowsingEquipment(false)}
        >
          <EquipmentBrowser
            character={character}
            updateCharacter={onCharacterUpdate}
            onManagerClose={() => modals.setIsBrowsingEquipment(false)}
          />
        </Modal>
      )}

      {modals.isFacingDeath && (
        <Modal
          title="💀 Death's Door"
          onClose={() => modals.setIsFacingDeath(false)}
        >
          <DeathManager
            character={character}
            updateCharacter={onCharacterUpdate}
            onManagerClose={() => modals.setIsFacingDeath(false)}
          />
        </Modal>
      )}

    </div>
    </PartyVitalsProvider>
  );
}
